import { Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    faCircleArrowLeft,
    faPaperPlane,
    faPenToSquare,
    faTrash,
    faCirclePlus,
    faCircleArrowRight,
    faSearch,
    faHouse,
    faBuilding,
    faHouseChimney,
} from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { MessageResponse } from '../_models/responses';
import { environment } from '../../environments/environment';
import { LawService } from '../_services';
import { PropertyService } from '../_services/property.service';
import { Property } from '../_models/property';

interface Law {
    id?: number;
    title: string;
    description: string;
    propertyId: number;
}

@Component({
    selector: 'app-manage-law',
    templateUrl: './manage-law.component.html',
    styleUrls: ['./manage-law.component.css'],
    standalone: false
})
export class ManageLawComponent {
    action: string = 'list';
    searchProperty = '';

    faCircleArrowLeft = faCircleArrowLeft;
    faPaperPlane = faPaperPlane;
    faPenToSquare = faPenToSquare;
    faTrash = faTrash;
    faCirclePlus = faCirclePlus;
    faCircleArrowRight = faCircleArrowRight;
    faSearch = faSearch;
    faHouse = faHouse;
    faBuilding = faBuilding;
    faHouseChimney = faHouseChimney;

    loading = false;
    fetching = false;
    submitLawData = false;

    searchLaw = '';
    loadingProperties = false;

    laws: Law[] = [];
    properties: Property[] = [];
    property: any;
    selectedLaw: Law | null = null;

    addLawForm!: FormGroup;
    editLawForm!: FormGroup;
    viewLawForm!: FormGroup;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private _location: Location,
        private formBuilder: FormBuilder,
        private lawService: LawService,
        private propertyService: PropertyService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.route.params.subscribe((params: Params) => {
            this.action = params['action'] || 'list';
            this.initializeForms();
            this.loadPropertyContextForAction();
        });
    }

    get fla() {
        return this.addLawForm.controls;
    }

    get fle() {
        return this.editLawForm.controls;
    }

    goBack(): void {
        this._location.back();
    }

    goHome(): void {
        this.router.navigate(['/']);
    }


    onCreateLaw(): void {
        this.router.navigate(['/manage-laws/create']);
    }

    onCancel(): void {
        this.router.navigate(['/manage-laws/list']);
    }

    onSelectedLaw(law: Law): void {
        this.selectedLaw = law;
        localStorage.setItem('law', JSON.stringify(law));

        this.viewLawForm.patchValue({
            id: law.id,
            title: law.title,
            description: law.description,
            propertyId: law.propertyId,
            propertyName: this.property?.name || '',
        });

        this.router.navigate(['/manage-laws/view']);
    }

    onEditLaw(): void {
        if (!this.selectedLaw) {
            this.toastr.error('No law selected for editing.', 'Error');
            return;
        }

        this.editLawForm.patchValue({
            id: this.selectedLaw.id,
            title: this.selectedLaw.title,
            description: this.selectedLaw.description,
            propertyId: this.selectedLaw.propertyId,
            propertyName: this.property?.name || '',
        });

        this.router.navigate(['/manage-laws/edit']);
    }

    createLaw(): void {
        this.submitLawData = true;
        this.fetching = true;

        if (!this.addLawForm.valid) {
            this.fetching = false;
            return;
        }

        const formdata = this.addLawForm.value;
        const payload = {
            title: formdata.title,
            description: formdata.description,
            propertyId: formdata.propertyId,
        };

        this.lawService.createLaw(payload).subscribe({
            next: (data: MessageResponse) => {
                this.toastr.success(data.message, 'Success');
                this.fetching = false;
                this.submitLawData = false;
                this.loadLawsByProperty();
                this.router.navigate(['/manage-laws/list']);
            },
            error: (error: HttpErrorResponse) => {
                this.fetching = false;
                this.toastr.error('Failed to create law.', 'Error');
                if (!environment.production) {
                    console.log('Law creation failed: ', error);
                }
            },
        });
    }

    updateLaw(): void {
        this.submitLawData = true;
        this.fetching = true;

        if (!this.editLawForm.valid) {
            this.fetching = false;
            return;
        }

        const formdata = this.editLawForm.value;
        const lawId = formdata.id;
        const payload = {
            title: formdata.title,
            description: formdata.description,
            propertyId: formdata.propertyId,
        };

        this.lawService.updateLaw(lawId, payload).subscribe({
            next: (data: MessageResponse) => {
                this.toastr.success(data.message, 'Success');
                this.fetching = false;
                this.submitLawData = false;
                localStorage.setItem('law', JSON.stringify({ id: lawId, ...payload }));
                this.loadLawsByProperty();
                this.router.navigate(['/manage-laws/list']);
            },
            error: (error: HttpErrorResponse) => {
                this.fetching = false;
                this.toastr.error('Failed to update law.', 'Error');
                if (!environment.production) {
                    console.log('Law update failed: ', error);
                }
            },
        });
    }

    onDeleteLaw(): void {
        const lawId = this.selectedLaw?.id || this.editLawForm?.value?.id || this.viewLawForm?.value?.id;
        if (!lawId) {
            this.toastr.error('No law selected for deletion.', 'Error');
            return;
        }

        this.fetching = true;
        this.lawService.deleteLaw(lawId).subscribe({
            next: (data: MessageResponse) => {
                this.toastr.success(data.message, 'Success');
                this.fetching = false;
                localStorage.removeItem('law');
                this.selectedLaw = null;
                this.loadLawsByProperty();
                this.router.navigate(['/manage-laws/list']);
            },
            error: (error: HttpErrorResponse) => {
                this.fetching = false;
                this.toastr.error('Failed to delete law.', 'Error');
                if (!environment.production) {
                    console.log('Law delete failed: ', error);
                }
            },
        });
    }

    private initializeForms(): void {
        this.addLawForm = this.formBuilder.group({
            title: ['', Validators.required],
            description: ['', Validators.required],
            propertyId: ['', Validators.required],
            propertyName: [''],
        });

        this.editLawForm = this.formBuilder.group({
            id: ['', Validators.required],
            title: ['', Validators.required],
            description: ['', Validators.required],
            propertyId: ['', Validators.required],
            propertyName: [''],
        });

        this.viewLawForm = this.formBuilder.group({
            id: [{ value: '', disabled: true }, Validators.required],
            title: [{ value: '', disabled: true }, Validators.required],
            description: [{ value: '', disabled: true }, Validators.required],
            propertyId: [{ value: '', disabled: true }, Validators.required],
            propertyName: [{ value: '', disabled: true }],
        });
    }

    private loadPropertyContextForAction(): void {
        this.loadingProperties = true;
        this.propertyService.getMyPropertyAssociations().subscribe({
            next: (associations: any[]) => {
                this.properties = associations.map(assoc => ({
                    id: assoc.propertyId,
                    name: assoc.propertyDetails.name,
                    property_type: assoc.propertyDetails.property_type,
                    address: assoc.propertyDetails.address,
                    obligationCategoryId: assoc.propertyDetails.obligationCategoryId ?? 0,
                    premises: []
                })) || [];

                if (this.properties.length === 1) {
                    this.setSelectedProperty(this.properties[0]);
                } else {
                    const propertystr = localStorage.getItem('property');
                    const currentProperty = propertystr ? JSON.parse(propertystr) : null;
                    if (currentProperty?.id) {
                        const matched = this.properties.find((p) => p.id === currentProperty.id);
                        if (matched) {
                            this.setSelectedProperty(matched);
                        }
                    }
                }

                if (this.properties.length > 1 && this.action === 'list') {
                    this.action = 'index';
                }

                this.loadingProperties = false;
                this.loadLawStateForAction();
            },
            error: () => {
                this.loadingProperties = false;
                this.toastr.error('Failed to load properties.', 'Error');
            },
        });
    }

    onSelectedProperty(property: Property): void {
        this.setSelectedProperty(property);
        this.action = 'list';
        this.loadLawStateForAction();
    }

    onChangeProperty(): void {
        this.action = 'index';
    }

    private setSelectedProperty(property: Property): void {
        this.property = property;
        this.propertyService.setProperty(property);
        this.addLawForm.patchValue({ propertyId: this.property.id, propertyName: this.property.name });
    }

    private loadLawStateForAction(): void {
        if (this.action === 'index') {
            return;
        }

        if (!this.property?.id) {
            return;
        }

        if (this.action === 'list') {
            this.loadLawsByProperty();
            return;
        }

        if (this.action === 'create') {
            this.addLawForm.patchValue({
                title: '',
                description: '',
                propertyId: this.property?.id || '',
                propertyName: this.property?.name || '',
            });
            return;
        }

        const lawstr = localStorage.getItem('law');
        this.selectedLaw = lawstr ? JSON.parse(lawstr) : null;

        if (!this.selectedLaw?.id) {
            this.toastr.warning('Select a law from the list first.', 'Info');
            this.router.navigate(['/manage-laws/list']);
            return;
        }

        this.lawService.getLaw(this.selectedLaw.id).subscribe({
            next: (law: Law) => {
                this.selectedLaw = law;
                localStorage.setItem('law', JSON.stringify(law));

                if (this.action === 'view') {
                    this.viewLawForm.patchValue({
                        id: law.id,
                        title: law.title,
                        description: law.description,
                        propertyId: law.propertyId,
                        propertyName: this.property?.name || '',
                    });
                }

                if (this.action === 'edit') {
                    this.editLawForm.patchValue({
                        id: law.id,
                        title: law.title,
                        description: law.description,
                        propertyId: law.propertyId,
                        propertyName: this.property?.name || '',
                    });
                }
            },
            error: (error: HttpErrorResponse) => {
                this.toastr.error('Failed to load selected law.', 'Error');
                if (!environment.production) {
                    console.log('Law fetch failed: ', error);
                }
                this.router.navigate(['/manage-laws/list']);
            },
        });
    }

    private loadLawsByProperty(): void {
        if (!this.property?.id) {
            this.laws = [];
            return;
        }

        this.loading = true;
        this.lawService.getLawsByPropertyId(this.property.id).subscribe({
            next: (laws: Law[]) => {
                this.laws = laws || [];
                this.loading = false;
            },
            error: (error: HttpErrorResponse) => {
                this.loading = false;
                this.toastr.error('Failed to load laws.', 'Error');
                if (!environment.production) {
                    console.log('Law list fetch failed: ', error);
                }
            },
        });
    }

}
